import { useMemo, useState, useCallback } from 'react'

import { useRefresh } from './auth/useRefresh'
import { isTokenExpired } from '../utils/jwtUtils'
import { ApiError, ErrorStatusModel } from '../types/exception/errorStatus'

type ContentType =
  | 'application/json'
  | 'application/x-www-form-urlencoded'
  | 'multipart/form-data'
  | 'application/json-patch+json'

const getHeaders = async (
  contentType?: ContentType,
  token?: string
): Promise<HeadersInit> => {
  const headers: HeadersInit = { accept: 'application/json' }

  if (contentType && contentType !== 'multipart/form-data') {
    headers['Content-Type'] = contentType
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  return headers
}

export function useFetch(token?: string, refreshToken?: string) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { refresh } = useRefresh()

  type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  type RequestBody = Record<string, unknown> | FormData

  const buildRequestBody = (body?: RequestBody): BodyInit | undefined => {
    if (!body) return undefined
    return body instanceof FormData ? body : JSON.stringify(body)
  }

  const toFallbackErrorStatus = (response: Response): ErrorStatusModel => ({
    status: response.status,
    error: response.statusText,
    message: '',
    messageKey: '',
    timestamp: new Date().toISOString(),
    traceId: '',
    details: undefined as any,
  })

  const parseErrorStatus = async (
    response: Response
  ): Promise<ErrorStatusModel> => {
    try {
      return (await response.json()) as ErrorStatusModel
    } catch {
      return toFallbackErrorStatus(response)
    }
  }

  const parseResponse = async <T>(response: Response): Promise<T | null> => {
    if (response.status === 204) return null

    const contentDisposition = response.headers.get('Content-Disposition')
    if (
      contentDisposition?.includes('inline') ||
      contentDisposition?.includes('attachment')
    ) {
      return response as unknown as T
    }

    const contentType = response.headers.get('Content-Type') ?? ''
    if (!contentType.includes('application/json')) return null

    try {
      return (await response.json()) as T
    } catch {
      return null
    }
  }

  const makeRequest = useCallback(
    async <T>(
      url: string,
      method: HttpMethod,
      body?: RequestBody,
      contentType?: ContentType,
      customToken?: string,
      retried = false
    ): Promise<T | null> => {
      setError(null)
      setLoading(true)

      let currentToken = customToken || token

      if (currentToken && isTokenExpired(currentToken) && !retried) {
        const newToken = await refresh()
        if (!newToken) {
          return null
        }

        currentToken = newToken
      }

      const headers = await getHeaders(contentType, currentToken)
      if (currentToken && isTokenExpired(currentToken) && !retried) {
        const newToken = await refresh()
        if (!newToken) return null
        currentToken = newToken
      }
      const response = await fetch(url, {
        method,
        headers,
        body: buildRequestBody(body),
      })

      if (!response.ok) {
        throw new ApiError(await parseErrorStatus(response))
      }

      return await parseResponse<T>(response)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, refreshToken]
  )

  return useMemo(
    () => ({
      loading,
      error,

      fetcher: async <T>(url: string): Promise<T | null> =>
        makeRequest<T>(url, 'GET'),
      poster: async <T>(
        url: string,
        body: any,
        contentType: ContentType,
        customToken?: string
      ) => makeRequest<T>(url, 'POST', body, contentType, customToken),
      putter: async <T>(url: string, body: any, contentType?: ContentType) =>
        makeRequest<T>(url, 'PUT', body, contentType),
      patcher: async <T>(url: string, body?: any, contentType?: ContentType) =>
        makeRequest<T>(url, 'PATCH', body, contentType),
      deleter: async <T>(url: string): Promise<T | null> =>
        makeRequest<T>(url, 'DELETE'),
    }),
    [loading, error, makeRequest]
  )
}
